import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type PageIntroProps = {
  eyebrow?: ReactNode;
  /** The plain part of the h1. */
  title: ReactNode;
  /** The one italic wine phrase, as in the home hero. */
  accent?: ReactNode;
  /** Puts the accent on its own line, like the home hero. */
  accentOnNewLine?: boolean;
  lead?: ReactNode;
  /** Actions or a small note under the lead (red pill, text link …). */
  children?: ReactNode;
  className?: string;
  /** Width of the text column; forms and documents use the narrow one. */
  width?: "narrow" | "wide";
};

/**
 * The top of a page without a photograph (contact, forms, checkout, legal,
 * blog index …): centred eyebrow, the h1 in the home hero's type — Playfair
 * semibold with one italic wine accent — and a calm lead. Every such page
 * starts the same distance below the header (the home's section rhythm), so
 * no page opens with a different gap. No falling petals here: they stay on
 * the home, location and about pages (decided 18.10.2026).
 */
export function PageIntro({
  eyebrow,
  title,
  accent,
  accentOnNewLine = false,
  lead,
  children,
  className,
  width = "narrow",
}: PageIntroProps) {
  return (
    <section className={cn("relative isolate bg-white", className)}>
      <div
        className={cn(
          "mx-auto px-5 pb-8 pt-10 text-center sm:px-8 sm:pb-10 sm:pt-14",
          width === "narrow" ? "max-w-3xl" : "max-w-5xl",
        )}
      >
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1
          className={cn(
            "text-balance font-display text-[1.75rem] font-semibold leading-[1.1] text-ink-900 sm:text-[2.05rem] lg:text-[2.3rem]",
            eyebrow ? "mt-3" : null,
          )}
        >
          {title}
          {accent ? (
            accentOnNewLine ? (
              <span className="mt-1 block font-normal italic text-coral-700">{accent}</span>
            ) : (
              <>
                {" "}
                <span className="font-normal italic text-coral-700">{accent}</span>
              </>
            )
          ) : null}
        </h1>
        {lead ? (
          <p className="mx-auto mt-4 max-w-xl text-pretty text-[15px] leading-[1.7] text-navy-600 sm:text-[16px]">
            {lead}
          </p>
        ) : null}
        {children ? (
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">{children}</div>
        ) : null}
      </div>
    </section>
  );
}
